import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { Plus, Edit, Trash2, Loader2, Target, ArrowLeft } from 'lucide-react';

const ManageStrategies = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentStrategy, setCurrentStrategy] = useState(null);
  const [formData, setFormData] = useState({ strategy_name: '', risk_profile: 'conservative', local_offshore_type: 'local', description: '', asset_allocation: {}, active: true });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/questions/strategies');
      setStrategies(response.data);
    } catch (error) {
      console.error('Failed to fetch strategies:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.strategy_name.trim()) newErrors.strategy_name = 'Strategy name is required';
    if (!formData.risk_profile) newErrors.risk_profile = 'Risk profile is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    
    // Validate asset allocation
    let totalAllocation = 0;
    for (const key in formData.asset_allocation) {
      const value = parseFloat(formData.asset_allocation[key]);
      if (isNaN(value) || value < 0 ) newErrors[`asset_allocation_${key}`] = 'Must be a non-negative number';
      totalAllocation += value;
    }
    if (Math.abs(totalAllocation - 100) > 0.01) { // Allow for floating point inaccuracies
      newErrors.asset_allocation_total = 'Total allocation must sum to 100%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveStrategy = async () => {
    if (!validateForm()) return;

    // Ensure asset allocation is in the correct format
    const formattedAllocation = {};
    for (const key in formData.asset_allocation) {
      formattedAllocation[key] = parseFloat(formData.asset_allocation[key]);
    }

    try {
      const dataToSubmit = { ...formData, asset_allocation: formattedAllocation };
      if (currentStrategy) {
        await api.put(`/questions/strategies/${currentStrategy.id}`, dataToSubmit);
      } else {
        await api.post('/questions/strategies', dataToSubmit);
      }
      setModalOpen(false);
      fetchStrategies();
    } catch (error) {
      console.error('Failed to save strategy:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to save strategy' });
    }
  };

  const handleEditStrategy = (strategy) => {
    setCurrentStrategy(strategy);
    // Ensure asset_allocation is initialized if missing or malformed
    const allocation = strategy.asset_allocation || {
      Cash: 0, 'Money Market': 0, 'Government Bonds': 0, 'Corporate Bonds': 0,
      Equities: 0, Property: 0, OtherCash: 0
    };
    setFormData({
      strategy_name: strategy.strategy_name,
      risk_profile: strategy.risk_profile,
      local_offshore_type: strategy.local_offshore_type,
      description: strategy.description,
      asset_allocation: allocation,
      active: strategy.active
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleNewStrategy = () => {
    setCurrentStrategy(null);
    setFormData({ 
      strategy_name: '', 
      risk_profile: 'conservative', 
      local_offshore_type: 'local', 
      description: '', 
      asset_allocation: { 
        'Cash': 0, 'Money Market': 0, 'Government Bonds': 0, 'Corporate Bonds': 0, 
        'Equities': 0, 'Property': 0, 'OtherCash': 0 
      }, 
      active: true 
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleDeleteStrategy = async (id) => {
    if (!window.confirm('Are you sure you want to delete this strategy?')) return;
    try {
      await api.delete(`/questions/strategies/${id}`);
      fetchStrategies();
    } catch (error) {
      console.error('Failed to delete strategy:', error);
    }
  };

  const riskProfileOptions = [
    { value: 'conservative', label: 'Conservative' },
    { value: 'moderate_conservative', label: 'Moderate Conservative' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'moderate_aggressive', label: 'Moderate Aggressive' },
    { value: 'aggressive', label: 'Aggressive' }
  ];

  const localOffshoreOptions = [
    { value: 'local', label: 'Local' },
    { value: 'offshore', label: 'Offshore' },
    { value: 'local_offshore', label: 'Local / Offshore Blend' }
  ];

  const handleAllocationChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      asset_allocation: {
        ...prev.asset_allocation,
        [key]: value
      }
    }));
  };

  const allocationKeys = Object.keys(formData.asset_allocation);

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Manage Investment Strategies</h1>
            </div>
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin Panel
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold" style={{ color: theme.text }}>Manage Investment Strategies</h2>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end mb-6">
            <Button onClick={handleNewStrategy}>
              <Plus className="w-5 h-5 mr-2" /> Add Strategy
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: theme.primary }} />
              <p className="text-lg" style={{ color: theme.textSecondary }}>Loading strategies...</p>
            </div>
          ) : strategies.length === 0 ? (
            <div className="text-center py-12">
              <Target className="w-16 h-16 mx-auto mb-4 opacity-20" style={{ color: theme.text }} />
              <p className="text-lg" style={{ color: theme.textSecondary }}>No strategies found</p>
            </div>
          ) : (
            <table className="min-w-full divide-y" style={{ borderColor: theme.border }}>
              <thead>
                <tr>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Name</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Risk Profile</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Type</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Active</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: theme.border }}>
                {strategies.map((s) => (
                  <tr key={s.id}>
                    <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.text }}>{s.strategy_name}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm capitalize" style={{ color: theme.text }}>{s.risk_profile}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm capitalize" style={{ color: theme.text }}>{s.local_offshore_type}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.text }}>{s.active ? 'Yes' : 'No'}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm space-x-2">
                      <button onClick={() => handleEditStrategy(s)} className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                        <Edit className="w-4 h-4 inline-block" />
                      </button>
                      <button onClick={() => handleDeleteStrategy(s.id)} className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                        <Trash2 className="w-4 h-4 inline-block" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      </main>

      {/* Modal for Add/Edit Strategy */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="max-w-3xl w-full">
            <Card>
              <CardHeader>
                <h2 className="text-2xl font-bold" style={{ color: theme.text }}>{currentStrategy ? 'Edit Strategy' : 'Add New Strategy'}</h2>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => { e.preventDefault(); handleSaveStrategy(); }} noValidate className="space-y-4">
                  {errors.submit && <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>}

                  <Input label="Strategy Name" value={formData.strategy_name} onChange={(e) => setFormData({ ...formData, strategy_name: e.target.value })} error={errors.strategy_name} required />
                  <Input label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} error={errors.description} required rows={3} as="textarea" />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Select label="Risk Profile" options={riskProfileOptions} value={formData.risk_profile} onChange={(e) => setFormData({ ...formData, risk_profile: e.target.value })} error={errors.risk_profile} required />
                    <Select label="Type" options={localOffshoreOptions} value={formData.local_offshore_type} onChange={(e) => setFormData({ ...formData, local_offshore_type: e.target.value })} error={errors.local_offshore_type} required />
                  </div>

                  <h4 className="text-lg font-semibold mt-6" style={{ color: theme.text }}>Asset Allocation (%)</h4>
                  {errors.asset_allocation_total && <p className="text-sm text-red-500 dark:text-red-400 mb-2">{errors.asset_allocation_total}</p>}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {allocationKeys.map(key => (
                      <Input 
                        key={key}
                        label={key} 
                        type="number" 
                        step="0.01"
                        value={formData.asset_allocation[key]} 
                        onChange={(e) => handleAllocationChange(key, e.target.value)} 
                        error={errors[`asset_allocation_${key}`]} 
                        required 
                      />
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="w-5 h-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium" style={{ color: theme.text }}>Is Active</span>
                    </label>
                    <div>
                      <Button type="button" variant="secondary" onClick={() => setModalOpen(false)} className="mr-3">Cancel</Button>
                      <Button type="submit">Save Strategy</Button>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageStrategies;
